<script setup lang="ts">
import { ref, watch, computed, storeToRefs } from '#imports';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useTippy, type TippyOptions } from 'vue-tippy';

import { usePanelActionStore } from './../store';
import { useDialogEventStore } from '@/widgets/dialog-action-event/store';
import { useDialogQuoteStore } from '@/widgets/dialog-action-quote/store';
import { useArticlesStore } from '@/entities/Article/store';

import DialogActionEvent from '@/widgets/dialog-action-event/components/dialog-action-event.vue';
import DialogActionFeedback from '@/widgets/dialog-action-feedback/components/dialog-action-feedback.vue';
import DialogActionQuote from '@/widgets/dialog-action-quote/components/dialog-action-quote.vue';
import ShareList from '@/widgets/share-list/share-list.vue';

import { BREAKPOINT } from '@/shared/constant';
import useResponsive from '@/shared/composables/use-responsive';

import IconDots from '@/assets/icons/icon-dots.svg';
import IconQuote from '@/assets/icons/icon-quote.svg';
import IconPrint from '@/assets/icons/icon-print.svg';
import IconShare from '@/assets/icons/icon-share.svg';
import IconEvent from '@/assets/icons/icon-event.svg';
import IconAttention from '@/assets/icons/icon-attention.svg';

const panelActionStore = usePanelActionStore();
const { dialog } = storeToRefs(panelActionStore);
const { showSpecialDialog, handlePrint } = panelActionStore;

const dialogEventStore = useDialogEventStore();
const { eventData } = storeToRefs(dialogEventStore);
const { getCurrentEvent, getEvents } = dialogEventStore;

const dialogQuoteStore = useDialogQuoteStore();
const { getBibliographyLinkFormats } = dialogQuoteStore;

await Promise.allSettled([
	getCurrentEvent(),
	getEvents(),
	getBibliographyLinkFormats(),
]);

const isOpen = ref(true);
const handleClose = () => isOpen.value = false;

const { isMobile, isTablet } = useResponsive();

const { width } = useWindowSize();
watch(width, (val) => {
	if (val >= BREAKPOINT.md) {
		isOpen.value = false;
	}
}, { immediate: true });

const computedClass = computed(() => ({
	'panel': true,
	'panel--round': !isOpen.value && !isTablet.value,
}));

const quote = ref();
const print = ref();
const share = ref();
const event = ref();
const feedback = ref();

const { state: stateEvent } = useTippy(event, {
	allowHTML: true,
	content: `${eventData.value?.name}`,
	placement: 'top-start',
	showOnCreate: !!eventData.value?.name,
	trigger: 'manual',
	theme: 'light panel',
});

const { state: stateFeedback } = useTippy(feedback, {
	allowHTML: true,
	content: 'Нашли ошибку?<br>Сообщите нам',
	placement: 'bottom-start',
	showOnCreate: true,
	trigger: 'manual',
	theme: 'light panel',
});

const initTippyButton = (content: any, options: TippyOptions = {}, type?: 'simple'): TippyOptions => {
	if (type === 'simple' && isMobile.value) {
		options['trigger'] = 'manual';
	}

	return {
		theme: 'light panel',
		placement: 'left',
		content,
		...options,
	};
};

let quoteTippy: any = null;
let printTippy: any = null;
let shareTippy: any = null;
let eventTippy: any = null;
let feedbackTippy: any = null;

watch(() => [stateEvent.value['isShown'], stateFeedback.value['isShown']], (val) => {
	if (val.filter(Boolean).length === 0) {
		quoteTippy = useTippy(quote, initTippyButton('Цитировать', {}, 'simple'));
		printTippy = useTippy(print, initTippyButton('Печать', {}, 'simple'));
		eventTippy = useTippy(event, initTippyButton('Посмотреть события', {}, 'simple'));
		feedbackTippy = useTippy(feedback, initTippyButton('Обратная связь', {}, 'simple'));
		shareTippy = useTippy(share, initTippyButton(ShareList, {
			interactive: true,
		}));
	}
});

if (process.client) {
	window.onbeforeprint = () => {
		[quoteTippy, printTippy, shareTippy, eventTippy, feedbackTippy].forEach((tippy) => {
			tippy.unmount();
			tippy.destroy();
		});
	};

	window.onafterprint = () => {
		setTimeout(() => {
			[quoteTippy, printTippy, shareTippy, eventTippy, feedbackTippy].forEach((tippy) => {
				tippy.mount();
			});
		}, 500);
	};
}

const articleStore = useArticlesStore();
const { isEmptyArticleId } = storeToRefs(articleStore);
</script>

<template>
	<div v-on-click-outside="handleClose" :class="computedClass">
		<button
			v-show="!isTablet && !isOpen"
			class="panel__list-button button"
			@click="isOpen = true"
		>
			<icon-dots class="panel__list-icon icon" />
		</button>

		<div v-show="isOpen || isTablet" class="panel__list">
			<div class="panel__list-item">
				<button
					ref="quote"
					:disabled="isEmptyArticleId"
					class="panel__list-button button"
					@click="showSpecialDialog('quote')"
				>
					<icon-quote class="panel__list-icon icon" />
				</button>
			</div>

			<div class="panel__list-item">
				<button
					ref="print"
					class="panel__list-button button"
					@click="handlePrint"
				>
					<icon-print class="panel__list-icon icon" />
				</button>
			</div>

			<div class="panel__list-item">
				<button
					ref="share"
					:disabled="isEmptyArticleId"
					class="panel__list-button button"
				>
					<icon-share class="panel__list-icon icon" />
				</button>
			</div>

			<div class="panel__list-item">
				<button
					ref="event"
					class="panel__list-button button"
					@click="showSpecialDialog('event')"
				>
					<icon-event class="panel__list-icon icon" />
				</button>
			</div>

			<div class="panel__list-item">
				<button
					ref="feedback"
					class="panel__list-button button"
					@click="showSpecialDialog('feedback')"
				>
					<icon-attention class="panel__list-icon icon" />
				</button>
			</div>
		</div>
	</div>

	<lazy-client-only>
		<dialog-action-quote v-model="dialog.quote" />
		<dialog-action-event v-model="dialog.event" />
		<dialog-action-feedback v-model="dialog.feedback" />
	</lazy-client-only>
</template>

<style lang="scss" scoped>
.panel {
	position: fixed;
	z-index: 100;
	right: 2%;
	bottom: 35%;

	padding: rem($gap-small) rem(12px);

	border-radius: rem($radius-secondary);
	background-color: $science-blue;

	@media print {
		display: none;
	}

	&--round {
		padding: rem(12px) rem(12px);

		border-radius: 50%;
	}

	&__list {
		display: flex;
		align-items: center;
		flex-direction: column;

		&-item {
			margin-bottom: rem(18px);

			@include reset-indent-last;
		}

		&-button {
			$size: 36px;

			display: flex;
			align-items: center;
			justify-content: center;

			padding: 0;

			transition: background-color $transition-duration $transition-function;

			color: $white;

			@include hover {
				background-color: darken($science-blue, 5%);
			}

			&[disabled] {
				cursor: not-allowed;

				color: grey;

				@include hover {
					background: none;
				}
			}
		}

		&-icon {
			$size: 28px;

			width: rem($size);
			height: rem($size);
		}
	}
}
</style>
